import { useMemo } from 'react';

import { useMy } from '@/hooks';
import { Status } from '@/sections/customerManagement/types';
import { CustomerTypeValue, SubscriptionConfiguration } from '@/types';
import { mergeStatus } from '@/utils/api';

/**
 * Calculate status from subscription dates and customer type
 */
function calculateStatusFromDates(
  startDate?: string,
  endDate?: string,
  customerType?: CustomerTypeValue
): Status {
  if (!startDate || !endDate) {
    return 'Setup'; // No subscription dates available
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999); // Set to end of day

  // Calculate days until start
  const daysUntilStart = Math.ceil(
    (start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Check if subscription has ended - only for pilot and engagement types
  if (
    today > end &&
    (customerType === 'PILOT' || customerType === 'ENGAGEMENT')
  ) {
    return 'Completed';
  }

  // Check if subscription hasn't started yet but is upcoming (within 30 days)
  if (today < start) {
    if (daysUntilStart <= 30) {
      return 'Upcoming';
    }
    // If more than 30 days away, treat as Setup
    return 'Setup';
  }

  // Current date is between start and end
  return 'Active';
}

/**
 * Shared hook for calculating customer status consistently across components
 * This ensures both the dashboard and drawer show the same status
 */
export function useCalculatedCustomerStatus(
  customerEmail: string,
  customerType?: CustomerTypeValue
) {
  // Query subscription configuration for this specific customer
  const configurationQuery = useMy(
    {
      resource: 'configuration',
    },
    { email: customerEmail }
  );

  // Query frozen status for this specific customer
  const frozenQuery = useMy(
    {
      resource: 'setting',
      query: 'frozen',
    },
    { email: customerEmail }
  );

  const status = useMemo(() => {
    // Extract subscription data from configuration
    const subscriptionConfig = configurationQuery.data?.find(
      item => item.name === 'subscription'
    );
    const subscription = subscriptionConfig?.value as
      | SubscriptionConfiguration
      | undefined;

    // Check if frozen
    const frozenSetting = frozenQuery.data?.find(
      item => item.name === 'frozen'
    );
    const isFrozen = frozenSetting?.value === 'true';

    // Calculate date-based status
    const dateStatus = calculateStatusFromDates(
      subscription?.startDate,
      subscription?.endDate,
      customerType
    );

    // If subscription has ended (Completed), that takes precedence
    if (dateStatus === 'Completed') {
      return 'Completed';
    }
    // If frozen flag is set and subscription hasn't ended, show as Paused
    else if (isFrozen) {
      return 'Paused';
    }
    // Otherwise use the calculated date-based status
    else {
      return dateStatus;
    }
  }, [configurationQuery.data, frozenQuery.data, customerType]);

  return {
    status,
    isLoading:
      mergeStatus(configurationQuery.status, frozenQuery.status) === 'pending',
    error: configurationQuery.error || frozenQuery.error,
  };
}

/**
 * Hook for calculating status for all customers (used in dashboard)
 * This is the original implementation for bulk operations
 */
export function useBulkCustomerStatus(
  emails: string[],
  customerTypes: Map<string, CustomerTypeValue>
) {
  // Query subscription configuration for all accounts
  const configurationQuery = useMy(
    {
      resource: 'configuration',
      allTenants: true,
    },
    {
      doNotImpersonate: true, // Customer management should always run as the actual user
    }
  );

  const configurationDataMap = useMemo(() => {
    const map = new Map<string, SubscriptionConfiguration>();
    configurationQuery.data?.forEach(
      (item: { name: string; value: unknown; username?: string }) => {
        if (item.name === 'subscription' && item.value && item.username) {
          const subscription = item.value as SubscriptionConfiguration;
          map.set(item.username, subscription);
        }
      }
    );
    return map;
  }, [configurationQuery.data]);

  // Query frozen status for all accounts
  const frozenQuery = useMy(
    {
      resource: 'setting',
      query: 'frozen',
      allTenants: true,
    },
    {
      doNotImpersonate: true, // Customer management should always run as the actual user
    }
  );

  const frozenDataMap = useMemo(
    () => new Map(frozenQuery.data?.map(item => [item.username, item]) || []),
    [frozenQuery.data]
  );

  const dataMap = useMemo(() => {
    const statusMap = new Map<string, Status>();

    emails.forEach(email => {
      const subscription = configurationDataMap.get(email) as
        | SubscriptionConfiguration
        | undefined;
      const frozenData = frozenDataMap.get(email);
      const isFrozen =
        frozenData &&
        typeof frozenData === 'object' &&
        'value' in frozenData &&
        frozenData.value === 'true';
      const customerType = customerTypes.get(email);

      let status: Status;

      // Calculate date-based status
      const dateStatus = calculateStatusFromDates(
        subscription?.startDate,
        subscription?.endDate,
        customerType
      );

      // Apply status precedence rules
      if (dateStatus === 'Completed') {
        status = 'Completed';
      } else if (isFrozen) {
        status = 'Paused';
      } else {
        status = dateStatus;
      }

      statusMap.set(email, status);
    });

    return statusMap;
  }, [emails, configurationDataMap, frozenDataMap, customerTypes]);

  return {
    data: dataMap,
    status: mergeStatus(configurationQuery.status, frozenQuery.status),
  };
}
